import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useBrands } from "../contexts/BrandContext";
import { useAuth } from "../contexts/AuthContext";
import {
  CalendarClock, ListChecks, Inbox, Users, UserX,
  MapPin, Layers, CheckCircle2, XCircle, CircleDashed, StickyNote,
} from "lucide-react";

function dayLabel(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("fr-FR", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function isPast(iso) {
  if (!iso) return false;
  const today = new Date().toISOString().slice(0, 10);
  return iso < today;
}

// Statut de réalisation aligné sur Agenda Départements (source de vérité UX).
const VALIDATION_META = {
  pending:   { label: "Planifiée",     color: "#0284C7", bg: "#0284C71A", icon: CircleDashed },
  completed: { label: "Effectuée",     color: "#059669", bg: "#0596691A", icon: CheckCircle2 },
  missed:    { label: "Non effectuée", color: "#DC2626", bg: "#DC26261A", icon: XCircle },
};
const getValidationMeta = (s) => VALIDATION_META[s] || VALIDATION_META.pending;

const FILTERS = [
  { id: "upcoming", label: "À venir", icon: CalendarClock },
  { id: "past", label: "Passées", icon: ListChecks },
  { id: "all", label: "Toutes", icon: Inbox },
];

export default function BeneficiaireInterventions({ beneficiaryId }) {
  const { brands } = useBrands();
  const { user } = useAuth();
  const canOpenDossier = ["admin", "manager", "gestionnaire"].includes(user?.role);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("upcoming");

  useEffect(() => {
    if (!beneficiaryId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/beneficiaries/${beneficiaryId}/interventions`);
        if (!cancelled) setEvents(data || []);
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [beneficiaryId]);

  const grouped = useMemo(() => {
    const sorted = [...events].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const upcoming = sorted.filter((e) => !isPast(e.date));
    const past = sorted.filter((e) => isPast(e.date)).reverse();
    return { upcoming, past, all: [...upcoming, ...past] };
  }, [events]);

  const list = grouped[filter] || [];

  return (
    <div data-testid="beneficiaire-interventions">
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="ben-interventions-filters">
        {FILTERS.map((f) => {
          const Icon = f.icon;
          const count = grouped[f.id]?.length ?? 0;
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setFilter(f.id)}
              data-testid={`ben-interv-filter-${f.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-sm transition-colors ${
                active
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
              }`}
            >
              <Icon size={14} />
              {f.label}
              <span
                className={`ml-1 text-[10px] font-mono ${active ? "text-zinc-200" : "text-zinc-500"}`}
              >
                ({count})
              </span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500 font-mono">
          Chargement…
        </div>
      ) : list.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500 font-mono">
          Aucune intervention {filter === "upcoming" ? "à venir" : filter === "past" ? "passée" : "enregistrée"}.
        </div>
      ) : (
        <ul className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100" data-testid="ben-interventions-list">
          {list.map((e) => {
            const br = brands.find((x) => x.id === e.brand_id);
            const past = isPast(e.date);
            const vMeta = getValidationMeta(e.validation_status);
            const VIcon = vMeta.icon;
            return (
              <li key={e.id} className="p-3 sm:p-4 flex flex-col gap-3" data-testid={`ben-interv-row-${e.id}`}>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div
                    className={`h-10 w-10 rounded-sm flex flex-col items-center justify-center flex-shrink-0 border ${
                      past ? "bg-zinc-50 border-zinc-200 text-zinc-500" : "bg-amber-50 border-amber-200 text-amber-700"
                    }`}
                  >
                    <span className="text-[10px] font-mono leading-none">
                      {e.date ? new Date(e.date + "T12:00:00").toLocaleDateString("fr-FR", { month: "short" }).replace(".", "") : "—"}
                    </span>
                    <span className="text-base font-bold leading-tight">
                      {e.date ? e.date.slice(8, 10) : "—"}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">
                      {e.title || "Intervention"}
                    </p>
                    <p className="text-[11px] text-zinc-500 font-mono mt-0.5">
                      {dayLabel(e.date)}
                      {(e.start_time || e.end_time) && ` · ${e.start_time || ""}${e.end_time ? "–" + e.end_time : ""}`}
                    </p>
                    {/* Détails de traçabilité : site + catégories, alignés sur Agenda Départements. */}
                    <div
                      className="flex flex-wrap items-center gap-1.5 mt-1.5"
                      data-testid={`ben-interv-details-${e.id}`}
                    >
                      {e.site_name && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-sm bg-cyan-50 border border-cyan-100 text-cyan-800"
                          data-testid={`ben-interv-site-${e.id}`}
                          title="Site / point de vente"
                        >
                          <MapPin size={10} /> {e.site_name}
                        </span>
                      )}
                      {e.intervention_category_name && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-sm bg-emerald-50 border border-emerald-100 text-emerald-800"
                          data-testid={`ben-interv-category-${e.id}`}
                          title="Catégorie d'intervention"
                        >
                          <Layers size={10} /> {e.intervention_category_name}
                        </span>
                      )}
                      {e.shift_category_name && !e.intervention_category_name && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-sm bg-emerald-50 border border-emerald-100 text-emerald-800"
                          data-testid={`ben-interv-shift-category-${e.id}`}
                          title="Catégorie de shift"
                        >
                          <Layers size={10} /> {e.shift_category_name}
                        </span>
                      )}
                    </div>
                    {e.notes && (
                      <p
                        className="text-xs text-zinc-600 mt-1 line-clamp-2 flex items-start gap-1"
                        data-testid={`ben-interv-notes-${e.id}`}
                      >
                        <StickyNote size={12} className="mt-0.5 flex-shrink-0 text-zinc-400" />
                        <span>{e.notes}</span>
                      </p>
                    )}
                    {e.validation_status === "missed" && e.missed_reason && (
                      <p
                        className="text-[11px] italic text-red-700 mt-1 truncate"
                        title={e.missed_reason}
                        data-testid={`ben-interv-missed-reason-${e.id}`}
                      >
                        Motif : {e.missed_reason}
                      </p>
                    )}
                    {(e.validation_status === "completed" || e.validation_status === "missed") && e.validation_by_name && (
                      <p
                        className="text-[10px] text-zinc-400 font-mono mt-0.5"
                        data-testid={`ben-interv-validation-by-${e.id}`}
                      >
                        Par {e.validation_by_name}
                        {e.validation_at && ` · ${new Date(e.validation_at).toLocaleDateString("fr-FR")}`}
                      </p>
                    )}
                  </div>
                  {br && (
                    <span className="text-[11px] font-mono inline-flex items-center gap-1 px-2 py-0.5 rounded-sm bg-zinc-50 border border-zinc-200 flex-shrink-0">
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: br.color }} />
                      {br.name}
                    </span>
                  )}
                  {/* Statut de réalisation (Planifiée / Effectuée / Non effectuée) */}
                  <span
                    className="text-[10px] font-mono uppercase tracking-wider inline-flex items-center gap-1 px-2 py-0.5 rounded-sm flex-shrink-0 border"
                    style={{ color: vMeta.color, backgroundColor: vMeta.bg, borderColor: vMeta.color + "33" }}
                    data-testid={`ben-interv-status-${e.id}`}
                    title={e.validation_status === "missed" && e.missed_reason ? `Motif : ${e.missed_reason}` : vMeta.label}
                  >
                    <VIcon size={11} /> {vMeta.label}
                  </span>
                </div>

                {/* Collaborateurs réellement affectés (cross-référence shifts) */}
                <div
                  className="pl-0 sm:pl-[3.25rem] flex flex-wrap items-center gap-1.5"
                  data-testid={`ben-interv-collabs-${e.id}`}
                >
                  {(e.collaborateurs || []).length === 0 ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-mono text-zinc-400">
                      <UserX size={11} /> Aucun collaborateur affecté
                    </span>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1 text-[11px] font-mono text-zinc-500">
                        <Users size={11} />
                        {e.collaborateurs.length} collab.
                      </span>
                      {e.collaborateurs.map((c) => {
                        const pillTitleParts = [
                          c.role_label || c.poste || "Collaborateur",
                          c.site_name ? `Site : ${c.site_name}` : null,
                          c.shift_category_name ? `Catégorie : ${c.shift_category_name}` : null,
                          c.start_time ? `${c.start_time}${c.end_time ? "–" + c.end_time : ""}` : null,
                        ].filter(Boolean);
                        const pillTitle = pillTitleParts.join(" · ");
                        const pillContent = (
                          <>
                            {c.name}
                            {c.start_time && (
                              <span className="text-blue-500/80 font-normal">· {c.start_time}</span>
                            )}
                          </>
                        );
                        const pillClass =
                          "inline-flex items-center gap-1 text-[11px] font-mono px-2 py-0.5 rounded-sm bg-blue-50 border border-blue-100 text-blue-800";
                        return canOpenDossier && c.id ? (
                          <Link
                            key={`${e.id}-${c.shift_id || c.id}`}
                            to={`/collaborateurs/${c.id}/dossier`}
                            title={pillTitle + " — ouvrir le dossier"}
                            className={`${pillClass} hover:bg-blue-100 hover:border-blue-200 underline-offset-2 hover:underline transition-colors`}
                            data-testid={`ben-interv-collab-${e.id}-${c.id}`}
                          >
                            {pillContent}
                          </Link>
                        ) : (
                          <span
                            key={`${e.id}-${c.shift_id || c.id}`}
                            title={pillTitle}
                            className={pillClass}
                            data-testid={`ben-interv-collab-${e.id}-${c.id}`}
                          >
                            {pillContent}
                          </span>
                        );
                      })}
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
